import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/Button';
import { telemetry } from '../../utils/telemetryLogger';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        telemetry.log('ERROR', 'REACT_BOUNDARY', `Component error boundary caught exception: ${error.message}`, {
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-[400px] flex items-center justify-center p-8">
                    <div className="text-center max-w-md bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl backdrop-blur-xl">
                        <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8 text-rose-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-100 mb-2">System Interruption</h2>
                        <p className="text-slate-400 text-sm mb-6">
                            An unexpected component error occurred. Safe fallback state is active.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button variant="outline" onClick={() => window.location.reload()}>
                                <RefreshCw size={16} className="mr-2" />
                                Reload Page
                            </Button>
                            <Button onClick={this.handleRetry}>
                                Try Again
                            </Button>
                        </div>
                        {this.state.error && (
                            <div className="mt-6 p-4 bg-slate-950 rounded-xl text-left border border-slate-800">
                                <p className="text-xs font-mono text-rose-400 break-all">
                                    {this.state.error.message}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
